import { useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchRunOutput,
  useRenderAndDownload,
  useRenderInline,
  useTemplateRuns,
  type RenderedOutput,
  type RenderFormat,
  type RenderMode,
  type ReportRunSummary,
} from '../../api/render.js';
import { useApi } from '../../api/client.js';
import { useReportTemplate } from '../../api/report-templates.js';
import { InlineRenderViewer } from './render-viewers/InlineRenderViewer.js';
import { FullscreenPreview } from './FullscreenPreview.js';
import { ParameterForm, parametersAreValid, useParameterValues } from './ParameterForm.js';

/**
 * RenderPanel — produce a real artifact from the current template
 * version, preview it inline, and keep a browsable history of the
 * last 25 runs. Every render lives in report_runs on the server,
 * tenant-isolated and audited.
 *
 * Layout:
 *   left  → format picker + run button (+ download affordance)
 *   center→ inline viewer for the latest / opened run
 *   right → render history list (re-view / re-run / download)
 */
export function RenderPanel({
  templateId,
  templateName,
  reportId,
  initialParams,
  onSaveDefaults,
  onSaveAsMine,
  saveCopyMode,
  leftSlot,
  pageOrientation,
  pageSize,
  onRenderStart,
  onParametersReady,
}: {
  templateId: string;
  templateName: string;
  /** Report instance id — enables remote_enum parameter dropdowns
   *  (CSAGE-205/236), whose options are fetched scoped to this report.
   *  Omitted in the template-authoring surface (no instance scope). */
  reportId?: string;
  /** Optional pre-fill for the parameter form. Used by the Reports
   *  (instance) viewer to seed the form with the report's
   *  default_parameters. Falls through to contract defaults when
   *  omitted. */
  initialParams?: Record<string, unknown>;
  /** When supplied, enables a "Save as defaults" button. The callback
   *  persists the current parameter values as the owning artefact's
   *  defaults (typically a Report instance's default_parameters).
   *  Only the button visibility is scoped; the panel doesn't own the
   *  comparison logic — callers decide what counts as "changed". */
  onSaveDefaults?: (values: Record<string, unknown>) => Promise<void>;
  /** CSAGE-353: when supplied, enables a "Save as my report" button beside
   *  "Save as defaults". Receives the same filtered current values, so the
   *  saved copy reruns with whatever the user has just changed. Distinct from
   *  onSaveDefaults: that edits the shared report in place, this creates a
   *  private copy the caller owns. Callers that have no notion of a personal
   *  copy (template authoring) simply omit it and no button appears. */
  onSaveAsMine?: (values: Record<string, unknown>, name?: string) => Promise<void>;
  /**
   * CSAGE-353: which action the save button represents.
   *   'copy' — this is a shared report; saving creates the caller's own copy, so
   *            they get a modal to name it (legacy's Copy → "Save As" flow).
   *   'save' — this already IS the caller's copy; saving updates it in place, so
   *            no modal: the parameters they are changing are already on screen.
   * Derived by the caller from the report's visibility. Omitted, no button.
   */
  saveCopyMode?: 'copy' | 'save';
  /** Parent-rendered content that slots into the leading edge of the
   *  toolbar — typically the report title, breadcrumb, tabs, and an
   *  actions menu. Integrating it into the render toolbar keeps the
   *  report detail from stacking three separate header strips. */
  /**
   * Content injected at the head of the toolbar.
   *
   * CSAGE-384: may be a render function receiving the parameter values
   * CURRENTLY ON SCREEN. The Subscriptions button lives in this slot, and a
   * subscription authored from a report the user has narrowed must inherit that
   * narrowing — the values live here, in RenderPanel, so the slot has to hand
   * them out. A plain ReactNode still works unchanged.
   */
  leftSlot?: React.ReactNode | ((values: Record<string, unknown>) => React.ReactNode);
  /** Page geometry for the "paper" surface that wraps the viewer.
   *  Lets a landscape tabloid report look landscape tabloid even when
   *  nothing has rendered yet. Falls back to the template's defaults. */
  pageOrientation?: 'portrait' | 'landscape';
  pageSize?: 'letter' | 'legal' | 'a4' | 'tabloid';
  /** Fires the moment the user clicks Run, before the network request
   *  goes out. The host page uses this to auto-collapse its list
   *  sidebar so the rendered output gets the full width — the user
   *  has signaled they care about the report, not the picker. */
  onRenderStart?: () => void;
  onParametersReady?: (values: Record<string, unknown>) => void;
}): JSX.Element {
  // Two render modes:
  //   live → interactive HTML (in-app toolbar: search/group/export/zoom)
  //   pdf  → the real print PDF, shown inline in the browser viewer
  //          (faithful paging/zoom/fit, exactly what'll print)
  // The legacy "Print preview" HTML-paginated mode is gone — PDF is fast
  // enough now and shows the same thing the printer will produce, so a
  // separate HTML-paged preview just added noise.
  type ViewMode = 'live' | 'pdf';
  const [viewMode, setViewMode] = useState<ViewMode>('live');
  const format: RenderFormat = viewMode === 'pdf' ? 'pdf' : 'html';
  const render = useRenderInline(templateId);
  const renderAndDownload = useRenderAndDownload(templateId);
  const { data: runs } = useTemplateRuns(templateId);
  const templateQuery = useReportTemplate(templateId);
  const tpl = templateQuery.data;
  const api = useApi();

  // Parameter form — seeds from the current version's declared
  // parameters contract and re-seeds on contract changes.
  const paramContract = useMemo(
    () => tpl?.currentVersion?.definition.parameters ?? [],
    [tpl?.currentVersion?.definition.parameters],
  );
  const params = useParameterValues(paramContract, initialParams ?? {});
  const parametersReady = !!tpl?.currentVersion && params.ready;
  const paramsValid = useMemo(
    () => parametersAreValid(paramContract, params.values),
    [paramContract, params.values],
  );

  const [activeOutput, setActiveOutput] = useState<RenderedOutput | null>(null);
  const [loadingRunId, setLoadingRunId] = useState<string | null>(null);
  const [loadRunError, setLoadRunError] = useState<string | null>(null);

  // Save-as-defaults bookkeeping: remember the last known "saved"
  // overrides so the Save button disables when the current form matches
  // what's already on the server. We start from the seed (initialParams)
  // and bump when the caller confirms a save.
  const [savedSnapshot, setSavedSnapshot] = useState<string>(() =>
    JSON.stringify(initialParams ?? {}),
  );
  useEffect(() => {
    setSavedSnapshot(JSON.stringify(initialParams ?? {}));
  }, [JSON.stringify(initialParams ?? {})]); // eslint-disable-line react-hooks/exhaustive-deps
  const savableValues = useMemo(
    () => pickSavableValues(paramContract, params.values),
    [paramContract, params.values],
  );
  useEffect(() => {
    if (parametersReady) onParametersReady?.(savableValues);
  }, [parametersReady, savableValues, onParametersReady]);
  const defaultsDirty = useMemo(
    () => JSON.stringify(savableValues) !== savedSnapshot,
    [savableValues, savedSnapshot],
  );
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [saveDefaultsNote, setSaveDefaultsNote] = useState<string | null>(null);
  // CSAGE-353: "Save as my report" — separate in-flight/note state from
  // "Save as defaults" so a failure in one never overwrites the other's
  // message and both buttons disable independently.
  const [savingMine, setSavingMine] = useState(false);
  const [saveMineNote, setSaveMineNote] = useState<string | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);

  // Render mode sent to the server. Live uses 'embed' (headless — no
  // baked toolbar) so the portal owns the chrome via EmbedHtmlViewer.
  // PDF uses 'page' for the paginated artifact.
  const mode: RenderMode = viewMode === 'live' ? 'embed' : 'page';

  // UI state for the redesigned layout.
  const [paramsOpen, setParamsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Auto-open the params dropdown the first time the contract loads
  // if any required param is unfilled — that's the case where a click
  // on Run would fail, so we nudge the operator to the fields.
  useEffect(() => {
    if (paramContract.length > 0 && !paramsValid && !paramsOpen) setParamsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramContract.length]);

  async function saveDefaults(): Promise<void> {
    if (!onSaveDefaults) return;
    setSavingDefaults(true);
    setSaveDefaultsNote(null);
    try {
      await onSaveDefaults(savableValues);
      setSavedSnapshot(JSON.stringify(savableValues));
      setSaveDefaultsNote('Saved');
      setTimeout(() => setSaveDefaultsNote(null), 2000);
    } catch (err) {
      setSaveDefaultsNote(`Save failed: ${(err as Error).message}`);
    } finally {
      setSavingDefaults(false);
    }
  }

  /**
   * CSAGE-353: save the current parameter values as a private copy of this
   * report. Mirrors saveDefaults' shape, including the transient confirmation,
   * but says where the copy went — the copy lands in a different part of the
   * nav than the report the user is looking at, so "Saved" alone would leave
   * them hunting for it.
   */
  async function saveAsMine(name?: string): Promise<void> {
    if (!onSaveAsMine) return;
    setSavingMine(true);
    setSaveMineNote(null);
    try {
      await onSaveAsMine(savableValues, name);
      setSaveMineNote(name ? 'Saved to My reports' : 'Saved');
      setTimeout(() => setSaveMineNote(null), 4000);
      setCopyOpen(false);
    } catch (err) {
      setSaveMineNote(`Couldn't save your copy: ${(err as Error).message}`);
    } finally {
      setSavingMine(false);
    }
  }

  // Swap in the newest render result as it arrives. RenderPanel owns
  // the blob lifecycle (the inline viewer used to revoke on unmount,
  // but that fought with the fullscreen modal — see InlineRenderViewer
  // header comment). Revoke the previous URL before adopting a new
  // one, and again on full panel unmount.
  useEffect(() => {
    if (render.data && render.data.runId !== activeOutput?.runId) {
      const previous = activeOutput;
      setActiveOutput(render.data);
      if (previous) {
        try {
          URL.revokeObjectURL(previous.blobUrl);
        } catch {
          /* noop */
        }
      }
    }
  }, [render.data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (activeOutput) {
        try {
          URL.revokeObjectURL(activeOutput.blobUrl);
        } catch {
          /* noop */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function openRun(run: ReportRunSummary): Promise<void> {
    if (!run.format || run.format === 'other') return;
    onRenderStart?.();
    if (activeOutput) {
      try {
        URL.revokeObjectURL(activeOutput.blobUrl);
      } catch {
        /* noop */
      }
    }
    setLoadingRunId(run.id);
    setLoadRunError(null);
    try {
      const out = await fetchRunOutput(api.fetchRaw.bind(api), run.id, run.format as RenderFormat);
      setActiveOutput(out);
    } catch (err) {
      setLoadRunError((err as Error).message);
    } finally {
      setLoadingRunId(null);
    }
  }

  // Export menu: user picks a format and we trigger a download-only render.
  // HTML is intentionally absent — the inline viewer already shows HTML.
  const exportFormats: Array<{ id: RenderFormat; label: string; sub: string; icon: JSX.Element }> =
    [
      { id: 'pdf', label: 'PDF', sub: 'Pixel-perfect print', icon: <PdfIcon /> },
      { id: 'xlsx', label: 'Excel', sub: 'Typed cells, frozen header', icon: <XlsIcon /> },
      { id: 'csv', label: 'CSV', sub: 'Universal, raw values', icon: <CsvIcon /> },
      { id: 'json', label: 'JSON', sub: 'Data only, machine-readable', icon: <JsonIcon /> },
    ];
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  useEffect(() => {
    if (!exportMenuOpen) return;
    const onDoc = (e: MouseEvent): void => {
      const tgt = e.target as HTMLElement;
      if (!tgt.closest('.render-export-menu')) setExportMenuOpen(false);
    };
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setExportMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [exportMenuOpen]);

  // Resolve page geometry for the "paper" surface. Parent-supplied
  // values win; otherwise fall back to the template settings.
  const resolvedOrientation =
    pageOrientation ?? (tpl?.pageOrientation as 'portrait' | 'landscape' | undefined) ?? 'portrait';
  const resolvedSize =
    pageSize ?? (tpl?.pageSize as 'letter' | 'legal' | 'a4' | 'tabloid' | undefined) ?? 'letter';

  // CSAGE-204: the chip reflects only user-facing (non-hidden) params. Hidden
  // params are auto-supplied and must not inflate the count (was 3/19 → now 3/3).
  const visibleParams = paramContract.filter((p) => !p.hidden);
  const requiredCount = visibleParams.filter((p) => p.required).length;
  const filledCount = visibleParams.filter((p) => {
    const v = params.values[p.name];
    return v !== undefined && v !== null && v !== '';
  }).length;

  const renderErrBody = render.error
    ? ((render.error as Error & { body?: unknown }).body as
        | { message?: string; code?: string; category?: string }
        | undefined)
    : undefined;
  const renderErrMessage = renderErrBody?.message || (render.error ? String(render.error) : null);

  function runNow(): void {
    onRenderStart?.();
    render.mutate({ format, params: params.values, mode });
  }

  // Only declared drill-through parameters express run intent; embed=1 does not.
  // Wait for both the template and its parameter seed before validating.
  const autoRanRef = useRef(false);
  useEffect(() => {
    if (autoRanRef.current || !parametersReady || !paramsValid) return;
    const query = new URLSearchParams(window.location.search);
    if (!paramContract.some((p) => !p.hidden && p.name !== 'embed' && query.has(p.name))) return;
    autoRanRef.current = true;
    runNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parametersReady, paramsValid, paramContract, params.values]);

  // CSAGE-124 follow-up: auto-rerun when the user toggles modes if a
  // stale-mode output is sitting in the viewer. Without this you'd
  // have to remember to click Run again to apply Print preview, and
  // the live render's filter input + sort cursor would still be
  // showing — confusing because they're not part of the print artifact.
  // We only re-render when there's a finished output whose mode
  // doesn't match the toggle, and no render is already in flight.
  useEffect(() => {
    if (!activeOutput || render.isPending) return;
    // Re-render when the displayed artifact no longer matches the chosen
    // view — either the format changed (html ↔ pdf) or the chrome mode
    // changed (live ↔ page).
    const formatMismatch = activeOutput.format !== format;
    const modeMismatch = !!activeOutput.mode && activeOutput.mode !== mode;
    if (formatMismatch || modeMismatch) {
      render.mutate({ format, params: params.values, mode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Panel chrome. 'live' is full-bleed (used for both headless embed
  // HTML view and the PDF inline viewer). 'page' is the legacy
  // centered-paper chrome — still surfaced when an old run from the
  // history pane was saved in page mode, but never selected by the
  // toggle (the Print Preview button was removed).
  const displayedMode: RenderMode = (() => {
    if (activeOutput?.format === 'pdf') return 'live';
    if (activeOutput?.mode === 'page') return 'page';
    return 'live';
  })();

  return (
    <div className={`render-panel render-panel--v3 render-panel--mode-${displayedMode}`}>
      {/* ── Single unified toolbar ──────────────────────────────── */}
      <div className="render-toolbar">
        {leftSlot && (
          <div className="render-toolbar-lead">
            {/* CSAGE-384: `savableValues`, the same set "Save as defaults" and
                "Save a copy" persist — the user's real parameter selection with
                unknown/blank entries dropped. */}
            {typeof leftSlot === 'function' ? leftSlot(savableValues) : leftSlot}
          </div>
        )}
        <div className="render-toolbar-spacer" />

        {/* Live ↔ PDF toggle. Live = in-app interactive HTML; PDF =
            the real print artifact (faithful to what'll print). The
            legacy "Print preview" HTML-paginated mode was removed —
            it duplicated PDF without adding signal. Switching mode
            does NOT auto-rerun. */}
        <div className="render-mode-toggle" role="group" aria-label="Render mode">
          <button
            type="button"
            className={`render-mode-pill ${viewMode === 'live' ? 'render-mode-pill--active' : ''}`}
            onClick={() => setViewMode('live')}
            aria-pressed={viewMode === 'live'}
            title="Interactive HTML — sortable, filterable, scrolling"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M2 2h8v8H2z" />
              <path d="M2 5h8" />
              <path d="M5 5v5" />
            </svg>
            Live
          </button>
          <button
            type="button"
            className={`render-mode-pill ${viewMode === 'pdf' ? 'render-mode-pill--active' : ''}`}
            onClick={() => setViewMode('pdf')}
            aria-pressed={viewMode === 'pdf'}
            title="Print preview — exactly how the report paginates and prints"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 1h4l2 2v8H3z" />
              <path d="M7 1v2h2" />
              <path d="M5 7h2" />
            </svg>
            Print
          </button>
        </div>

        {paramContract.length > 0 && (
          <button
            type="button"
            className={`render-toolbar-params ${paramsOpen ? 'render-toolbar-params--open' : ''} ${!paramsValid ? 'render-toolbar-params--invalid' : ''}`}
            onClick={() => setParamsOpen((v) => !v)}
            aria-expanded={paramsOpen}
            title={paramsValid ? 'Show / edit parameters' : 'Fill in required parameters'}
          >
            <span className="render-toolbar-params-label">
              Parameters
              <span className="render-toolbar-params-count">
                {filledCount}/{visibleParams.length}
                {requiredCount > 0 && !paramsValid && (
                  <span className="render-toolbar-params-dot" aria-hidden="true">
                    ●
                  </span>
                )}
              </span>
            </span>
            <svg
              className="render-toolbar-caret"
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {paramsOpen ? <path d="M2 4l3 3 3-3" /> : <path d="M2 6l3-3 3 3" />}
            </svg>
          </button>
        )}

        <div className="render-toolbar-actions">
          <button
            type="button"
            className="btn btn--primary"
            disabled={render.isPending || !parametersReady || !paramsValid}
            onClick={runNow}
            title={paramsValid ? 'Render the report in the viewer' : 'Fill in required parameters'}
          >
            {render.isPending ? (
              <>
                <Spinner /> Running…
              </>
            ) : (
              <>▶ Run</>
            )}
          </button>

          {/* CSAGE-353: keeping a copy sits immediately right of Run, which is
            where the legacy module put Copy relative to the report — the two
            actions belong to the same "I've set this up how I want it" moment.
            It was first placed in the parameter-panel footer, which hid it
            entirely: `paramsOpen` starts false. */}
          {onSaveAsMine && saveCopyMode && (
            <button
              type="button"
              // Full-size + success green so it carries the same weight as Run and
              // reads as a distinct choice rather than a second indigo primary.
              className="btn btn--success"
              disabled={savingMine || !parametersReady || !paramsValid}
              onClick={() => (saveCopyMode === 'copy' ? setCopyOpen(true) : void saveAsMine())}
              title={
                !paramsValid
                  ? 'Fill in required parameters first'
                  : saveCopyMode === 'copy'
                    ? 'Save these parameter values as your own copy of this report'
                    : 'Save these parameter values to your copy'
              }
            >
              {savingMine ? 'Saving…' : saveCopyMode === 'copy' ? 'Save a copy' : 'Save changes'}
            </button>
          )}
          {saveMineNote && (
            <span
              role="status"
              className={saveMineNote.startsWith("Couldn't") ? 'text-danger' : 'text-dim'}
              style={{ fontSize: 'var(--font-size-sm)' }}
            >
              {saveMineNote}
            </span>
          )}

          <div className="render-export-menu">
            <button
              type="button"
              className="btn"
              disabled={renderAndDownload.isPending || !parametersReady || !paramsValid}
              onClick={() => setExportMenuOpen((v) => !v)}
              aria-expanded={exportMenuOpen}
              title="Export the report in a downloadable format"
            >
              {renderAndDownload.isPending ? 'Exporting…' : 'Export ▾'}
            </button>
            {exportMenuOpen && (
              <div className="render-export-menu-pop" role="menu">
                <div className="render-export-menu-title">Download as</div>
                {exportFormats.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    role="menuitem"
                    className="render-export-menu-item"
                    onClick={() => {
                      setExportMenuOpen(false);
                      renderAndDownload.mutate({
                        format: f.id,
                        templateName,
                        params: params.values,
                      });
                    }}
                  >
                    <span className="render-export-menu-item-icon">{f.icon}</span>
                    <span className="render-export-menu-item-meta">
                      <span className="render-export-menu-item-label">{f.label}</span>
                      <span className="render-export-menu-item-sub">{f.sub}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            className="btn-icon"
            title={
              activeOutput ? 'Open full-screen preview' : 'Run a render first to fullscreen it'
            }
            disabled={!activeOutput}
            onClick={() => setIsFullscreen(true)}
            aria-label="Full-screen preview"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1.5 5V1.5H5" />
              <path d="M9 1.5h3.5V5" />
              <path d="M12.5 9v3.5H9" />
              <path d="M5 12.5H1.5V9" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Collapsible params panel ─────────────────────────────── */}
      {paramsOpen && paramContract.length > 0 && (
        <>
          <div
            className="render-params-scrim"
            onClick={() => setParamsOpen(false)}
            aria-hidden="true"
          />
          <aside className="render-params-panel" role="dialog" aria-label="Parameters">
            <div className="render-params-panel-head">
              <span className="render-params-panel-title">Parameters</span>
              <button
                type="button"
                className="render-params-panel-close"
                onClick={() => setParamsOpen(false)}
                aria-label="Close parameters"
              >
                ✕
              </button>
            </div>
            <div className="render-params-panel-body">
              <ParameterForm
                parameters={paramContract}
                values={params.values}
                onChange={params.setValues}
                {...(reportId ? { reportId } : {})}
                compact
              />
              {saveDefaultsNote && (
                <div
                  className={`form-hint ${saveDefaultsNote.startsWith('Save failed') ? 'form-hint--error' : ''}`}
                >
                  {saveDefaultsNote}
                </div>
              )}
              {!paramsValid && (
                <div className="render-params-warn">
                  Fill in required fields (marked ●) before running.
                </div>
              )}
            </div>
            <div className="render-params-panel-foot">
              <button type="button" className="btn-link btn-link--sm" onClick={params.reset}>
                Reset
              </button>
              {onSaveDefaults && (
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={!defaultsDirty || savingDefaults}
                  onClick={saveDefaults}
                  title={defaultsDirty ? 'Persist these values as defaults' : 'Already saved'}
                >
                  {savingDefaults ? 'Saving…' : defaultsDirty ? 'Save as defaults' : 'Saved'}
                </button>
              )}
              <button
                type="button"
                className="btn btn--primary render-params-run"
                disabled={render.isPending || !parametersReady || !paramsValid}
                onClick={() => {
                  runNow();
                  setParamsOpen(false);
                }}
                title={paramsValid ? 'Render the report' : 'Fill in required parameters'}
              >
                {render.isPending ? (
                  <>
                    <Spinner /> Running…
                  </>
                ) : (
                  <>▶ Run</>
                )}
              </button>
            </div>
          </aside>
        </>
      )}

      {templateQuery.isError && (
        <div className="render-error-strip" role="alert">
          Report settings could not load.{' '}
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => void templateQuery.refetch()}
          >
            Try again
          </button>
        </div>
      )}
      {renderAndDownload.error && (
        <div className="render-error-strip" role="alert">
          Export failed: {renderAndDownload.error.message}. Please try Export again.
        </div>
      )}
      {/* ── Viewer (always HTML; empty/error states render inside the paper) ── */}
      <section className="render-viewer-col">
        {/* CSAGE-200: the rows·MB·ms stats meta-line was removed — it added
            noise above the report without carrying user-facing value. */}
        <div className="render-viewer-stage">
          <div
            className={
              'render-paper' +
              ` render-paper--${resolvedOrientation}` +
              ` render-paper--${resolvedSize}` +
              (activeOutput ? ' render-paper--filled' : ' render-paper--empty')
            }
            role="document"
            aria-label={`${templateName} preview`}
          >
            {render.isPending ? (
              <ReportPlaceholder kind="loading" />
            ) : renderErrMessage ? (
              <ReportPlaceholder kind="error" message={renderErrMessage} />
            ) : activeOutput ? (
              <InlineRenderViewer
                output={activeOutput}
                paper={{ orientation: resolvedOrientation, size: resolvedSize }}
                reportTitle={templateName}
              />
            ) : (
              <ReportPlaceholder kind="empty" />
            )}
          </div>
        </div>
      </section>

      {loadRunError && (
        <div className="render-error-strip render-error-strip--warn" role="alert">
          <span className="render-error-kicker">History</span>
          <span className="render-error-msg">Failed to load run: {loadRunError}</span>
        </div>
      )}

      {/* ── Recent runs (collapsible grid at the bottom) ────────── */}
      <div className={`render-history-bar ${historyOpen ? 'render-history-bar--open' : ''}`}>
        <button
          type="button"
          className="render-history-toggle"
          aria-expanded={historyOpen}
          onClick={() => setHistoryOpen((v) => !v)}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {historyOpen ? <path d="M2 4l3 3 3-3" /> : <path d="M4 2l3 3-3 3" />}
          </svg>
          <span className="render-history-toggle-label">Recent runs</span>
          <span className="render-history-toggle-count">{runs?.length ?? 0}</span>
          {runs && runs[0] && !historyOpen && (
            <span className="render-history-toggle-hint">
              last: {formatRelative(runs[0].startedAt)} · v{runs[0].versionUsed ?? '—'} ·{' '}
              {(runs[0].rowCount ?? 0).toLocaleString()} rows
            </span>
          )}
        </button>

        {historyOpen &&
          (!runs || runs.length === 0 ? (
            <div className="text-dim text-sm" style={{ padding: '10px 16px' }}>
              No runs yet.
            </div>
          ) : (
            <div className="render-history-grid-wrap">
              <table className="render-history-grid">
                <thead>
                  <tr>
                    <th scope="col" className="rhg-col-format">
                      Format
                    </th>
                    <th scope="col" className="rhg-col-time">
                      When
                    </th>
                    <th scope="col" className="rhg-col-ver">
                      Ver
                    </th>
                    <th scope="col" className="rhg-col-rows">
                      Rows
                    </th>
                    <th scope="col" className="rhg-col-dur">
                      Duration
                    </th>
                    <th scope="col" className="rhg-col-status">
                      Status
                    </th>
                    <th scope="col" className="rhg-col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.slice(0, 25).map((r) => {
                    const active = activeOutput?.runId === r.id;
                    return (
                      <tr key={r.id} className={'rhg-row' + (active ? ' rhg-row--active' : '')}>
                        <td className="rhg-col-format">
                          <span className={'badge badge--' + statusBadgeClass(r.status)}>
                            {(r.format ?? '?').toUpperCase()}
                          </span>
                        </td>
                        <td className="rhg-col-time">{formatRelative(r.startedAt)}</td>
                        <td className="rhg-col-ver text-dim">v{r.versionUsed ?? '—'}</td>
                        <td className="rhg-col-rows num">{(r.rowCount ?? 0).toLocaleString()}</td>
                        <td className="rhg-col-dur num">
                          {r.durationMs !== null ? `${r.durationMs} ms` : '—'}
                        </td>
                        <td className="rhg-col-status">
                          <span className="text-sm">{r.status ?? '—'}</span>
                        </td>
                        <td className="rhg-col-actions">
                          <button
                            type="button"
                            className="btn-link btn-link--sm"
                            onClick={() => void openRun(r)}
                            disabled={loadingRunId === r.id || !r.outputUri}
                            title={
                              r.outputUri ? 'Preview this run in the viewer' : 'Run had no output'
                            }
                          >
                            {loadingRunId === r.id ? 'Loading…' : 'Open'}
                          </button>
                          {Object.keys(r.params ?? {}).length > 0 && (
                            <button
                              type="button"
                              className="btn-link btn-link--sm"
                              onClick={() => params.setValues(r.params)}
                              title="Copy this run's params into the form"
                            >
                              Use params
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
      </div>

      {/* ── Fullscreen modal ─────────────────────────────────────── */}
      {isFullscreen && activeOutput && (
        <FullscreenPreview
          onClose={() => setIsFullscreen(false)}
          title={`${templateName} · ${activeOutput.format.toUpperCase()} · ${activeOutput.rowCount.toLocaleString()} rows`}
        >
          <InlineRenderViewer
            output={activeOutput}
            paper={{ orientation: resolvedOrientation, size: resolvedSize }}
            reportTitle={templateName}
          />
        </FullscreenPreview>
      )}

      {/* ── Save-a-copy modal (CSAGE-353) ────────────────────────── */}
      {copyOpen && (
        <SaveCopyDialog
          defaultName={templateName}
          parameters={paramContract}
          values={savableValues}
          saving={savingMine}
          error={saveMineNote?.startsWith("Couldn't") ? saveMineNote : null}
          onCancel={() => setCopyOpen(false)}
          onSave={(name) => void saveAsMine(name)}
        />
      )}

      <RenderStyles />
    </div>
  );
}

/**
 * Name-and-confirm dialog for "Save a copy" (CSAGE-353).
 *
 * Mirrors the legacy module's Copy → "Save As" flow, which customers already
 * know: a pre-filled name and the parameters the copy will carry. One deliberate
 * departure — the parameters are shown READ-ONLY rather than re-editable.
 *
 * Legacy had to make them editable because its Copy button lived on the list
 * row, so you never saw the report's parameters before copying. Here you have
 * just been adjusting them in the panel behind this dialog, and offering a second
 * editable copy of the same fields would create two sources of truth for one
 * set of values. The summary confirms what is being captured instead.
 *
 * The name defaults to the report's own name, exactly as legacy pre-filled it.
 * That used to be impossible: `create()` upserts on (tenant, template, name), so
 * saving under the identical name rewrote the shared report instead of copying
 * it. Migration 049 made that unique index partial (excluding personal rows), so
 * a copy may now keep the name it came from — which is what users expect, since
 * "Uptime with my parameters" is still the Uptime report.
 */
function SaveCopyDialog({
  defaultName,
  parameters,
  values,
  saving,
  error,
  onCancel,
  onSave,
}: {
  defaultName: string;
  parameters: ReadonlyArray<{ name: string; label?: string; hidden?: boolean }>;
  values: Record<string, unknown>;
  saving: boolean;
  error: string | null;
  onCancel(): void;
  onSave(name: string): void;
}): JSX.Element {
  const [name, setName] = useState(defaultName);
  const trimmed = name.trim();
  // Two filters, both load-bearing:
  //   !p.hidden  — the same predicate ParameterForm uses (CAP3-8861). Without it
  //                the summary exposed plumbing the user cannot see or change,
  //                e.g. Canopy Client ID / Canopy Client Name. Anything absent
  //                from the parameter form has no business appearing in a
  //                confirmation of that form.
  //   has value  — so the summary can't promise a value the copy won't carry.
  const shown = parameters.filter((p) => {
    if (p.hidden) return false;
    const v = values[p.name];
    return v !== undefined && v !== null && v !== '';
  });
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 520 }}>
        <header className="modal-header">
          <div>
            <h2>Save a copy</h2>
            <div className="modal-subtitle">
              Keeps your own copy of this report. It appears under My Reports.
            </div>
          </div>
          <button className="btn-icon" type="button" onClick={onCancel} aria-label="Close">
            ×
          </button>
        </header>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmed) onSave(trimmed);
          }}
        >
          <div className="modal-body stack">
            <label className="field">
              <span className="field-label">Name</span>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                required
              />
            </label>
            {shown.length === 0 ? (
              // No visible parameters is normal — plenty of reports take none, and
              // a report whose only params are hidden plumbing looks the same to
              // the user. Say what will happen rather than showing an empty
              // "Parameters" heading with nothing under it.
              <div className="text-dim text-sm">
                This report has no parameters to set, so the copy will run exactly as this one does.
              </div>
            ) : (
              <div className="field">
                <span className="field-label">Parameters saved with this copy</span>
                <dl className="stack" style={{ gap: 2, margin: 0 }}>
                  {shown.map((p) => (
                    <div key={p.name} className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                      <dt className="text-mute text-xs" style={{ minWidth: 160 }}>
                        {p.label ?? p.name}
                      </dt>
                      <dd className="text-sm" style={{ margin: 0 }}>
                        {String(values[p.name])}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            {error && <div className="form-hint form-hint--error">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn--ghost" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={!trimmed || saving}>
              {saving ? 'Saving…' : 'Save a copy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function statusBadgeClass(status: string): string {
  if (status === 'completed' || status === 'success') return 'success';
  if (status === 'running' || status === 'pending') return 'accent';
  if (status === 'failed' || status === 'error') return 'danger';
  return 'neutral';
}

/**
 * Filter the current form values down to what's worth persisting as
 * defaults. Strips empties (null / '' / undefined) so a report's saved
 * defaults don't carry junk placeholders, and drops values that aren't
 * part of the current parameter contract (belt-and-suspenders against
 * stale entries).
 */
function pickSavableValues(
  contract: ReadonlyArray<{ name: string; kind: string }>,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const knownNames = new Set(contract.map((p) => p.name));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values)) {
    if (!knownNames.has(k)) continue;
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    out[k] = v;
  }
  return out;
}

function ReportPlaceholder({
  kind,
  message,
}: {
  kind: 'empty' | 'loading' | 'error';
  message?: string;
}): JSX.Element {
  const copy =
    kind === 'loading'
      ? {
          icon: <Spinner />,
          title: 'Rendering…',
          sub: 'The report is being generated. This can take a few seconds for large result sets.',
        }
      : kind === 'error'
        ? {
            icon: '⚠',
            title: 'This report could not be rendered',
            sub: message ?? 'An unexpected error occurred. Check your parameters and try again.',
          }
        : {
            icon: '◰',
            title: 'No render yet',
            sub: 'Click Run in the toolbar above to render this report. Adjust parameters first if you need a different scope.',
          };
  return (
    <div className={`render-paper-placeholder render-paper-placeholder--${kind}`}>
      <div className="render-paper-placeholder-icon" aria-hidden="true">
        {copy.icon}
      </div>
      <h3 className="render-paper-placeholder-title">{copy.title}</h3>
      <p className="render-paper-placeholder-sub">{copy.sub}</p>
    </div>
  );
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diffSec = Math.round((Date.now() - t) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

function Spinner(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      style={{ animation: 'spin 0.8s linear infinite' }}
    >
      <path d="M21 12a9 9 0 11-6.219-8.56" strokeLinecap="round" />
    </svg>
  );
}

function PdfIcon(): JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6" />
      <text
        x="7"
        y="17"
        fontSize="4"
        fill="currentColor"
        stroke="none"
        style={{ fontFamily: 'monospace' }}
      >
        PDF
      </text>
    </svg>
  );
}

function XlsIcon(): JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="16" rx="1.5" />
      <path d="M3 9h18M3 14h18M9 4v16M15 4v16" />
    </svg>
  );
}

function CsvIcon(): JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

function JsonIcon(): JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M7 4c-2 0-3 1-3 3v3c0 1.5-.5 2-2 2 1.5 0 2 .5 2 2v3c0 2 1 3 3 3" />
      <path d="M17 4c2 0 3 1 3 3v3c0 1.5.5 2 2 2-1.5 0-2 .5-2 2v3c0 2-1 3-3 3" />
    </svg>
  );
}

function RenderStyles(): JSX.Element {
  return (
    <style>{`
      /* ── v3 layout: leftSlot + toolbar → params → paper surface → history ── */
      .render-panel.render-panel--v3 {
        display: flex;
        flex-direction: column;
        gap: 0;
        /* Anchor for the parameters drawer + scrim (absolute, full-height). */
        position: relative;
        /* Fill the viewport from just under the app header to the bottom.
         * App chrome takes ~56-64px; DrillBreadcrumb (if shown) takes a
         * few more. We leave a small buffer and let the inner stage
         * expand freely. */
        height: calc(100vh - 72px);
        min-height: 640px;
        padding: 0;
        background: var(--color-surface-2);
      }
      .render-panel--v3 .render-toolbar {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: 6px var(--space-4);
        border-bottom: 1px solid var(--color-border-subtle);
        background: var(--color-surface-1);
        flex-wrap: wrap;
        flex: none;
      }
      .render-toolbar-actions { display: contents; }
      .render-toolbar-lead {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        min-width: 0;
        flex: 1 1 auto;
      }
      .render-panel--v3 .render-toolbar-spacer { flex: 0 1 var(--space-2); }

      /* Stage holds the report. In live mode it's a flat continuous
       * surface — no centering, no padding — so the iframe consumes the
       * whole recovered width when the sidebar collapses. In page mode
       * we keep gentle padding + centering so the simulated paper sits
       * inside the gray void with a frame around it. */
      .render-viewer-stage {
        flex: 1;
        min-height: 0;
        overflow: auto;
        display: flex;
        align-items: stretch;
        justify-content: stretch;
      }
      .render-panel--mode-page .render-viewer-stage {
        padding: 14px 18px;
        align-items: center;
        justify-content: center;
      }

      /* Filled "paper" wrapper. Live = flat full-width host, no card.
       * Page = continuing to defer paper styling to the iframe's own
       * page-chrome (it draws its own gray surround + paper card). */
      .render-paper {
        position: relative;
        display: flex;
        overflow: hidden;
      }
      .render-paper > * { flex: 1; min-width: 0; min-height: 0; }
      .render-panel--mode-live .render-paper--filled {
        width: 100%;
        height: 100%;
        max-width: none;
        background: var(--color-surface-1);
        border: 0;
        border-radius: 0;
        box-shadow: none;
      }
      .render-panel--mode-page .render-paper--filled {
        width: 100%;
        height: 100%;
        max-width: none;
        background: transparent;
        border: 0;
        border-radius: 0;
        box-shadow: none;
      }

      /* ── Empty / error / loading — honour the page's real aspect
       * ratio so a landscape report looks landscape and a portrait
       * report looks portrait even with no data.
       *
       * The paper is set to the biggest page-shaped rectangle that
       * fits the stage. aspect-ratio does the work; max-width +
       * max-height clamp the two dimensions to the available box so
       * whichever runs out first wins and the other shrinks
       * proportionally. height:100% makes align-items:center still
       * size us to the full stage height before clamping.
       */
      .render-paper--empty {
        width: 100%;
        height: 100%;
        max-height: 100%;
      }
      /* Aspect-ratio paper-shape preview for empty state — only when
       * the panel is in page mode. The previous selector was
       * unprefixed (3-class specificity) which beat any live-mode
       * override (2-class) and pinned the empty paper to the configured
       * paper size, leaving it flush-left in a wide stage. Scoping
       * under .render-panel--mode-page (4 classes total) guarantees
       * live mode's override below wins cleanly. */
      .render-panel--mode-page .render-paper--empty.render-paper--portrait.render-paper--letter  { aspect-ratio:  8.5 / 11;   max-width: min(100%, calc(100vh * 0.773)); }
      .render-panel--mode-page .render-paper--empty.render-paper--portrait.render-paper--legal   { aspect-ratio:  8.5 / 14;   max-width: min(100%, calc(100vh * 0.607)); }
      .render-panel--mode-page .render-paper--empty.render-paper--portrait.render-paper--a4      { aspect-ratio: 210 / 297;   max-width: min(100%, calc(100vh * 0.707)); }
      .render-panel--mode-page .render-paper--empty.render-paper--portrait.render-paper--tabloid { aspect-ratio:  11 / 17;    max-width: min(100%, calc(100vh * 0.647)); }
      .render-panel--mode-page .render-paper--empty.render-paper--landscape.render-paper--letter  { aspect-ratio:  11 / 8.5;  max-width: min(100%, calc(100vh * 1.294)); }
      .render-panel--mode-page .render-paper--empty.render-paper--landscape.render-paper--legal   { aspect-ratio:  14 / 8.5;  max-width: min(100%, calc(100vh * 1.647)); }
      .render-panel--mode-page .render-paper--empty.render-paper--landscape.render-paper--a4      { aspect-ratio: 297 / 210;  max-width: min(100%, calc(100vh * 1.414)); }
      .render-panel--mode-page .render-paper--empty.render-paper--landscape.render-paper--tabloid { aspect-ratio:  17 / 11;   max-width: min(100%, calc(100vh * 1.545)); }
      /* Live mode empty: drop the paper-shape preview — live is a
       * continuous experience, not a sheet. Fill the full stage so
       * the placeholder centers in the actual canvas. */
      .render-panel--mode-live .render-paper--empty {
        aspect-ratio: auto;
        max-width: none;
        background: transparent;
        border: 0;
        box-shadow: none;
      }

      /* ── With content — stretch vertically, let the iframe claim
       * all the height. Width is governed by mode-specific rules
       * above (live = full-bleed, page = full-width host that the
       * iframe internals fill with their own paper sim). */
      .render-paper--filled {
        width: 100%;
        height: 100%;
        min-height: 0;
      }

      /* Placeholder inside the paper keeps the "this is a report" feel */
      .render-paper-placeholder {
        flex: 1;
        width: 100%;
        min-width: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: var(--space-6);
        gap: 6px;
        color: var(--color-text-tertiary);
      }
      /* In live mode the placeholder lives directly on the panel
       * surface (no paper card). Pin it to the centre of the stage
       * regardless of upstream flex direction. */
      .render-panel--mode-live .render-paper--empty {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .render-paper-placeholder-icon {
        width: 52px; height: 52px;
        display: inline-flex; align-items: center; justify-content: center;
        font-size: 30px;
        color: var(--color-text-tertiary);
        background: var(--color-surface-2);
        border-radius: 999px;
        margin-bottom: var(--space-2);
      }
      .render-paper-placeholder--error .render-paper-placeholder-icon {
        color: #b91c1c; background: #fee2e2;
      }
      .render-paper-placeholder-title {
        font-size: var(--font-size-md);
        font-weight: var(--font-weight-semibold);
        color: var(--color-text-primary);
        margin: 0;
      }
      .render-paper-placeholder-sub {
        font-size: var(--font-size-sm);
        color: var(--color-text-tertiary);
        margin: 0;
        max-width: 480px;
        line-height: 1.55;
      }

      /* The viewer-subhead above the paper becomes an unobtrusive meta line */
      .render-panel--v3 .render-viewer-subhead {
        padding: 6px var(--space-4);
        font-size: var(--font-size-xs);
        color: var(--color-text-tertiary);
        background: transparent;
        border: 0;
      }

      /* CSAGE-124: render-mode segmented toggle */
      .render-panel--v3 .render-mode-toggle {
        display: inline-flex;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm);
        padding: 2px;
        gap: 1px;
      }
      .render-panel--v3 .render-mode-pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 4px 10px;
        background: transparent;
        border: 0;
        border-radius: calc(var(--radius-sm) - 1px);
        font: inherit;
        font-size: var(--font-size-sm);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: background var(--duration-fast) var(--ease-out),
                    color var(--duration-fast) var(--ease-out);
      }
      .render-panel--v3 .render-mode-pill:hover { color: var(--color-text-primary); }
      .render-panel--v3 .render-mode-pill--active {
        background: var(--color-surface-1);
        color: var(--color-text-primary);
        box-shadow: 0 1px 2px rgba(0,0,0,0.06);
      }
      .render-panel--v3 .render-mode-pill svg { color: var(--color-text-tertiary); flex: none; }
      .render-panel--v3 .render-mode-pill--active svg { color: var(--color-accent, #0b1220); }

      /* Parameters button — Stripe-style ghost with count chip */
      .render-panel--v3 .render-toolbar-params {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 6px 12px;
        background: var(--color-surface-1);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-sm);
        font: inherit;
        font-size: var(--font-size-sm);
        color: var(--color-text-primary);
        cursor: pointer;
        transition: border-color var(--duration-fast) var(--ease-out),
                    background var(--duration-fast) var(--ease-out);
      }
      .render-panel--v3 .render-toolbar-params:hover { background: var(--color-surface-2); }
      .render-panel--v3 .render-toolbar-params--open {
        border-color: var(--color-accent-border);
        background: var(--color-accent-softer);
      }
      .render-panel--v3 .render-toolbar-params--invalid { border-color: #f59e0b; }
      .render-panel--v3 .render-toolbar-params-count {
        font-size: 11px;
        color: var(--color-text-tertiary);
        background: var(--color-surface-2);
        padding: 1px 7px;
        border-radius: 999px;
      }
      .render-toolbar-params-dot { color: #f59e0b; margin-left: 2px; }
      .render-toolbar-caret { color: var(--color-text-tertiary); flex: none; }

      /* Export dropdown */
      .render-export-menu { position: relative; }
      .render-export-menu-pop {
        position: absolute; top: calc(100% + 4px); right: 0;
        min-width: 240px;
        background: var(--color-surface-1);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-md);
        box-shadow: var(--shadow-lg);
        padding: 4px;
        z-index: 30;
        display: flex; flex-direction: column;
      }
      .render-export-menu-title {
        font-size: 10px; font-weight: 650;
        letter-spacing: 0.1em; text-transform: uppercase;
        color: var(--color-text-tertiary);
        padding: 8px 10px 4px;
      }
      .render-export-menu-item {
        display: flex; align-items: center; gap: 10px;
        padding: 8px 10px;
        background: transparent; border: 0;
        text-align: left;
        font: inherit;
        color: var(--color-text-primary);
        border-radius: var(--radius-sm);
        cursor: pointer;
      }
      .render-export-menu-item:hover { background: var(--color-surface-2); }
      .render-export-menu-item-icon {
        width: 28px; height: 28px;
        display: inline-flex; align-items: center; justify-content: center;
        color: var(--color-text-secondary);
        background: var(--color-surface-2);
        border-radius: 6px;
        flex: none;
      }
      .render-export-menu-item-meta { display: flex; flex-direction: column; gap: 1px; }
      .render-export-menu-item-label { font-size: var(--font-size-sm); font-weight: var(--font-weight-medium); }
      .render-export-menu-item-sub   { font-size: 11px; color: var(--color-text-tertiary); }

      /* Params drawer — right-edge overlay (Stripe-like form) with a dim
       * scrim. Overlays the report rather than pushing it, so the narrow
       * embed keeps its width. Sticky head + footer; only the field list
       * scrolls. */
      .render-panel--v3 .render-params-scrim {
        position: absolute; inset: 0;
        background: rgba(0, 0, 0, 0.28);
        z-index: 40;
        animation: render-params-scrim-in 120ms ease-out;
      }
      @keyframes render-params-scrim-in { from { opacity: 0; } to { opacity: 1; } }
      .render-panel--v3 .render-params-panel {
        position: absolute; top: 0; right: 0; bottom: 0;
        width: min(380px, 92%);
        z-index: 41;
        background: var(--color-surface-1);
        border-left: 1px solid var(--color-border);
        border-bottom: 0;
        box-shadow: var(--shadow-lg);
        display: flex; flex-direction: column;
        /* Override the legacy global .render-params-panel { max-height: 38vh }
         * below — the drawer must fill the panel top to bottom, not be capped. */
        max-height: none;
        animation: render-params-panel-in 160ms ease-out;
      }
      @keyframes render-params-panel-in {
        from { transform: translateX(16px); opacity: 0.6; }
        to { transform: translateX(0); opacity: 1; }
      }
      .render-panel--v3 .render-params-panel-head {
        flex: none;
        display: flex; align-items: center; justify-content: space-between;
        padding: var(--space-3) var(--space-4);
        border-bottom: 1px solid var(--color-border-subtle);
      }
      .render-panel--v3 .render-params-panel-body {
        flex: 1; min-height: 0; overflow-y: auto;
        padding: var(--space-4);
        /* Force a visible scrollbar so the long tail of params reads as
         * "scroll for more", not "where did they go?". */
        scrollbar-gutter: stable;
      }
      .render-panel--v3 .render-params-panel-foot {
        flex: none;
        display: flex; align-items: center; gap: var(--space-2);
        padding: var(--space-3) var(--space-4);
        border-top: 1px solid var(--color-border-subtle);
        background: var(--color-surface-1);
      }
      .render-panel--v3 .render-params-panel-foot .render-params-run { margin-left: auto; }
      .render-panel--v3 .render-params-panel-close {
        background: transparent; border: 0; cursor: pointer;
        color: var(--color-text-tertiary);
        font-size: 15px; line-height: 1;
        padding: 4px 7px; border-radius: var(--radius-sm);
      }
      .render-panel--v3 .render-params-panel-close:hover {
        background: var(--color-surface-2); color: var(--color-text-primary);
      }
      /* In the portal embed the layout is a flex chain (.split-layout flex:1 →
       * section flex-column). The standalone calc(100vh - 72px) panel height
       * fights that chain and leaves the drawer short. Flex-fill instead: the
       * report-detail wrapper and the panel each take the full height of their
       * flex parent, so the panel spans the section and its absolute drawer
       * (top:0/bottom:0) fills it edge to edge. */
      [data-nav='embedded'] .report-detail.report-detail--full {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
      }
      [data-nav='embedded'] .report-detail--full .render-panel.render-panel--v3 {
        height: auto;
        flex: 1;
        min-height: 0;
      }
      .render-panel--v3 .render-params-panel-title {
        font-size: 11px; font-weight: 650;
        letter-spacing: 0.08em; text-transform: uppercase;
        color: var(--color-text-tertiary);
      }

      /* Recent runs — collapsible grid. Collapsed by default so the
       * paper claims the full vertical real estate. Toggle footer
       * stays visible and shows the last-run hint inline. */
      .render-history-bar {
        flex: none;
        border-top: 1px solid var(--color-border-subtle);
        background: var(--color-surface-1);
        display: flex;
        flex-direction: column;
        max-height: 38vh;
      }
      .render-history-toggle {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 7px var(--space-4);
        background: transparent;
        border: 0;
        font: inherit;
        color: var(--color-text-secondary);
        text-align: left;
        cursor: pointer;
      }
      .render-history-toggle:hover { background: var(--color-surface-2); }
      .render-history-toggle-label {
        font-size: 10px;
        font-weight: var(--font-weight-semibold);
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      .render-history-toggle-count {
        font-size: 11px;
        background: var(--color-surface-2);
        color: var(--color-text-tertiary);
        padding: 1px 7px;
        border-radius: 999px;
      }
      .render-history-toggle-hint {
        margin-left: auto;
        font-size: var(--font-size-xs);
        color: var(--color-text-tertiary);
      }

      .render-history-grid-wrap {
        overflow-y: auto;
        border-top: 1px solid var(--color-border-subtle);
      }
      .render-history-grid {
        width: 100%;
        border-collapse: collapse;
        font-size: var(--font-size-sm);
        table-layout: fixed;
      }
      .render-history-grid thead th {
        position: sticky;
        top: 0;
        background: var(--color-surface-2);
        z-index: 1;
        font-size: 10px;
        font-weight: var(--font-weight-semibold);
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--color-text-tertiary);
        text-align: left;
        padding: 6px 10px;
        border-bottom: 1px solid var(--color-border-subtle);
      }
      .render-history-grid tbody td {
        padding: 6px 10px;
        border-bottom: 1px solid var(--color-border-subtle);
        vertical-align: middle;
        color: var(--color-text-primary);
      }
      .render-history-grid tbody tr.rhg-row:hover td { background: var(--color-surface-2); }
      .render-history-grid tbody tr.rhg-row--active td {
        background: var(--color-accent-softer);
      }
      .render-history-grid .num {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }
      .rhg-col-format    { width: 70px; }
      .rhg-col-time      { width: 130px; }
      .rhg-col-ver       { width: 55px; }
      .rhg-col-rows      { width: 110px; }
      .rhg-col-dur       { width: 100px; }
      .rhg-col-status    { width: 110px; }
      .rhg-col-actions   { width: 140px; text-align: right; }
      .rhg-col-actions .btn-link { margin-left: 8px; }

      /* Error strip below the paper — non-intrusive */
      .render-panel--v3 .render-error-strip {
        display: flex; align-items: center; gap: var(--space-2);
        padding: 8px var(--space-4);
        background: #fef3c7; border-top: 1px solid #fde68a;
        font-size: var(--font-size-sm); color: #92400e;
        flex: none;
      }
      .render-panel--v3 .render-error-kicker {
        font-size: 9.5px; font-weight: 650;
        letter-spacing: 0.1em; text-transform: uppercase;
        padding: 2px 7px; background: rgba(0,0,0,0.08); border-radius: 4px;
      }

      /* ── old v2 (kept for other callers) ───────────────────────── */
      .render-panel.render-panel--v2 {
        display: flex;
        flex-direction: column;
        gap: 0;
        height: calc(100vh - 260px);
        min-height: 640px;
        padding: 0;
      }
      .render-toolbar {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-4);
        border-bottom: 1px solid var(--color-border-subtle);
        background: var(--color-surface-1);
        flex-wrap: wrap;
        flex: none;
      }
      .render-toolbar-spacer { flex: 1; min-width: var(--space-2); }

      .render-toolbar-formats {
        display: inline-flex;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
        padding: 3px;
        gap: 1px;
      }
      .render-format-pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 5px 10px;
        background: transparent;
        border: 0;
        border-radius: var(--radius-sm);
        font: inherit;
        font-size: var(--font-size-sm);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: background var(--duration-fast) var(--ease-out),
                    color var(--duration-fast) var(--ease-out);
      }
      .render-format-pill:hover { color: var(--color-text-primary); }
      .render-format-pill--active {
        background: var(--color-surface-1);
        color: var(--color-text-primary);
        box-shadow: 0 1px 2px rgba(0,0,0,0.06);
      }
      .render-format-pill-icon {
        width: 14px; height: 14px;
        display: inline-flex; align-items: center; justify-content: center;
        color: var(--color-text-tertiary);
      }
      .render-format-pill--active .render-format-pill-icon { color: var(--color-accent, #0b1220); }
      .render-format-pill-label { font-weight: var(--font-weight-medium); }

      .render-toolbar-params {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 6px 12px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm);
        font: inherit;
        font-size: var(--font-size-sm);
        color: var(--color-text-primary);
        cursor: pointer;
        transition: border-color var(--duration-fast) var(--ease-out);
      }
      .render-toolbar-params:hover { border-color: var(--color-border-strong, var(--color-border)); }
      .render-toolbar-params--open {
        background: var(--color-surface-1);
        border-color: var(--color-accent-border);
      }
      .render-toolbar-params--invalid { border-color: #f59e0b; }
      .render-toolbar-params-label { display: inline-flex; align-items: center; gap: 8px; }
      .render-toolbar-params-count {
        display: inline-flex; align-items: center; gap: 4px;
        font-size: 11px;
        color: var(--color-text-tertiary);
        background: var(--color-surface-1);
        padding: 1px 8px;
        border-radius: 999px;
        border: 1px solid var(--color-border-subtle);
      }
      .render-toolbar-params-dot { color: #f59e0b; margin-left: 2px; }
      .render-toolbar-caret { color: var(--color-text-tertiary); flex: none; }

      .render-params-panel {
        padding: var(--space-3) var(--space-4) var(--space-4);
        background: var(--color-surface-2);
        border-bottom: 1px solid var(--color-border-subtle);
        flex: none;
        max-height: 38vh;
        overflow-y: auto;
      }
      .render-params-panel-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-3);
        margin-bottom: var(--space-2);
      }
      .render-params-panel-title {
        font-size: 10px;
        font-weight: var(--font-weight-semibold);
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--color-text-tertiary);
      }
      .render-params-panel-actions { display: inline-flex; align-items: center; gap: var(--space-2); }
      .render-params-warn {
        margin-top: var(--space-2);
        font-size: var(--font-size-xs);
        color: #92400e;
        background: #fef3c7;
        border-radius: var(--radius-sm);
        padding: 6px 10px;
        display: inline-block;
      }

      .render-error-strip {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: 8px var(--space-4);
        background: #fee2e2;
        border-bottom: 1px solid #fecaca;
        font-size: var(--font-size-sm);
        color: #991b1b;
        flex: none;
      }
      .render-error-strip--warn { background: #fef3c7; border-bottom-color: #fde68a; color: #92400e; }
      .render-error-kicker {
        font-size: 9.5px; font-weight: var(--font-weight-semibold);
        letter-spacing: 0.1em; text-transform: uppercase;
        padding: 2px 7px; background: rgba(0,0,0,0.08); border-radius: 4px;
      }
      .render-error-msg { word-break: break-word; }

      .render-panel--v2 .render-viewer-col {
        flex: 1; min-height: 0;
        display: flex; flex-direction: column;
      }
      .render-viewer-subhead {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: 6px var(--space-4);
        border-bottom: 1px solid var(--color-border-subtle);
        background: var(--color-surface-1);
        font-size: var(--font-size-sm);
        flex: none;
      }
      .render-viewer-body--full {
        flex: 1; min-height: 0;
        overflow: auto;
        background: var(--color-surface-2);
      }

      .render-history-strip {
        flex: none;
        border-top: 1px solid var(--color-border-subtle);
        background: var(--color-surface-1);
        max-height: 140px;
        display: flex;
        flex-direction: column;
      }
      .render-history-strip-head {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px var(--space-4);
        border-bottom: 1px dashed var(--color-border-subtle);
      }
      .render-history-strip-title {
        font-size: 10px;
        font-weight: var(--font-weight-semibold);
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--color-text-tertiary);
      }
      .render-history-strip-list {
        list-style: none; margin: 0; padding: 8px var(--space-4);
        display: flex; gap: 8px;
        overflow-x: auto;
      }
      .render-history-chip {
        flex: none;
        display: inline-flex; align-items: stretch;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm);
        overflow: hidden;
        transition: border-color var(--duration-fast) var(--ease-out);
      }
      .render-history-chip:hover { border-color: var(--color-accent-border); }
      .render-history-chip--active {
        border-color: var(--color-accent-border);
        background: var(--color-accent-softer);
      }
      .render-history-chip-main {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 4px 8px 4px 6px;
        background: transparent; border: 0;
        font: inherit; color: inherit; cursor: pointer;
        min-width: 0;
      }
      .render-history-chip-main:disabled { opacity: 0.5; cursor: not-allowed; }
      .render-history-chip-format {
        font-size: 9px; letter-spacing: 0.06em;
      }
      .render-history-chip-meta {
        display: flex; flex-direction: column; gap: 0;
        font-size: 11px; text-align: left;
        min-width: 0;
      }
      .render-history-chip-meta > span:first-child { font-weight: var(--font-weight-medium); }
      .render-history-chip-use {
        padding: 0 10px;
        background: transparent;
        border: 0; border-left: 1px dashed var(--color-border-subtle);
        font: inherit; font-size: 11px;
        color: var(--color-text-tertiary);
        cursor: pointer;
        transition: color var(--duration-fast) var(--ease-out);
      }
      .render-history-chip-use:hover { color: var(--color-text-primary); }

      /* ── legacy (kept for fallback until cleanup) ─────────────── */
      .render-panel:not(.render-panel--v2) .render-layout {
        display: grid;
        grid-template-columns: 260px 1fr 280px;
        gap: 0;
        height: calc(100vh - 260px);
        min-height: 640px;
      }
      .render-sidebar {
        border-right: 1px solid var(--color-border-subtle);
        padding: var(--space-4);
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }
      .render-format-stack {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      .render-format-row {
        display: grid;
        grid-template-columns: 36px 1fr;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        background: var(--color-surface-1);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm);
        color: var(--color-text);
        text-align: left;
        cursor: pointer;
        align-items: center;
        transition: border-color 0.12s, background 0.12s;
      }
      .render-format-row:hover {
        border-color: var(--color-accent);
        background: var(--color-surface-2);
      }
      .render-format-row--active {
        border-color: var(--color-accent);
        background: var(--color-surface-3);
        box-shadow: 0 0 0 1px var(--color-accent);
      }
      .render-format-meta {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .render-format-label {
        font-weight: 600;
        font-size: var(--font-size-sm);
      }
      .render-format-sub {
        font-size: 11px;
        color: var(--color-text-dim);
      }
      .render-format-icon {
        width: 32px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--color-accent);
        border-radius: 6px;
        background: var(--color-surface-2);
      }
      .render-format-row--active .render-format-icon {
        background: var(--color-accent);
        color: white;
      }
      .render-actions {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-top: var(--space-3);
      }
      .render-viewer-col {
        display: flex;
        flex-direction: column;
        min-width: 0;
        /* CSAGE-197: height-bound the column so .render-viewer-stage's
           overflow:auto engages and the report (+ its horizontal scrollbar)
           scrolls internally instead of pushing the page past the nav rails. */
        flex: 1;
        min-height: 0;
      }
      .render-viewer-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--space-2) var(--space-4);
        border-bottom: 1px solid var(--color-border-subtle);
        background: var(--color-surface-1);
      }
      .render-viewer-title .badge { margin-right: 6px; }
      .render-viewer-body {
        flex: 1;
        overflow: auto;
        background: var(--color-surface-0);
      }
      .render-empty {
        padding: var(--space-6);
        text-align: center;
        color: var(--color-text-dim);
      }
      .render-empty-art {
        font-size: 36px;
        margin-bottom: var(--space-2);
      }
      .render-history {
        border-left: 1px solid var(--color-border-subtle);
        padding: var(--space-3);
        overflow-y: auto;
        background: var(--color-surface-1);
      }
      .render-history-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--space-2);
      }
      .render-history-head h3 {
        margin: 0;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-text-secondary);
      }
      .render-history-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .render-history-item--active .render-history-row {
        border-color: var(--color-accent);
        background: var(--color-surface-3);
      }
      .render-history-row {
        display: grid;
        grid-template-columns: 56px 1fr auto;
        gap: 8px;
        padding: 6px 8px;
        border: 1px solid var(--color-border-subtle);
        border-radius: 6px;
        background: var(--color-surface-2);
        color: var(--color-text);
        cursor: pointer;
        text-align: left;
        width: 100%;
        align-items: center;
        transition: border-color 0.12s, background 0.12s;
      }
      .render-history-row:hover:not(:disabled) {
        border-color: var(--color-accent);
      }
      .render-history-row:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .render-history-format {
        font-size: 10px;
        text-align: center;
      }
      .render-history-meta {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }
      .render-history-time {
        font-size: 12px;
        font-weight: 600;
      }
      .render-history-sub {
        font-size: 10px;
        color: var(--color-text-dim);
      }

      /* Inline viewer sub-styles */
      .render-viewer { width: 100%; height: 100%; }
      .render-viewer--csv, .render-viewer--json, .render-viewer--xlsx, .render-viewer--binary {
        padding: var(--space-3) var(--space-4);
      }
      .render-viewer-meta {
        font-size: 11px;
        color: var(--color-text-dim);
        margin-bottom: 6px;
      }
      .render-viewer-table-wrap {
        overflow: auto;
        max-height: 640px;
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm);
      }
      .render-viewer-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;
      }
      .render-viewer-table th {
        position: sticky;
        top: 0;
        background: var(--color-surface-2);
        text-align: left;
        padding: 6px 8px;
        border-bottom: 1px solid var(--color-border-subtle);
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--color-text-secondary);
      }
      .render-viewer-table td {
        padding: 5px 8px;
        border-bottom: 1px solid var(--color-border-subtle);
      }
      .sort-caret { color: var(--color-accent); margin-left: 4px; }
      .render-viewer-overflow {
        padding: 8px;
        font-size: 11px;
        color: var(--color-text-dim);
        text-align: center;
      }
      .render-viewer-raw {
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: 4px;
        padding: var(--space-2);
        max-height: 600px;
        overflow: auto;
        font-size: 11px;
      }
      .render-viewer-fallback {
        padding: var(--space-5);
        text-align: center;
      }
      .render-viewer-fallback-icon {
        color: var(--color-accent);
        margin-bottom: var(--space-3);
      }
      .render-viewer-json-tree {
        font-family: var(--font-mono);
        font-size: 12px;
        line-height: 1.5;
      }
      .json-block, .json-leaf { padding-left: 4px; }
      .json-toggle {
        background: transparent;
        border: 0;
        color: inherit;
        cursor: pointer;
        font: inherit;
        padding: 0;
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }
      .json-caret { width: 10px; color: var(--color-text-dim); }
      .json-key { color: var(--color-accent); margin-right: 4px; }
      .json-muted { color: var(--color-text-dim); }
      .json-str { color: var(--color-success); }
      .json-num { color: var(--color-warning); }
      .json-bool { color: #9c27b0; } /* canopy accent-purple */
      .json-null { color: var(--color-text-tertiary); font-style: italic; }
      .json-children {
        padding-left: 16px;
        border-left: 1px dashed var(--color-border-subtle);
      }

      /* Parameter form inside the render sidebar */
      .render-params {
        border-top: 1px solid var(--color-border-subtle);
        padding-top: var(--space-3);
        margin-top: var(--space-2);
      }
      .render-params-header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        margin-bottom: var(--space-2);
      }
      .render-params-label {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-text-secondary);
        font-weight: 600;
      }
      .btn-link {
        background: transparent;
        border: 0;
        color: var(--color-accent);
        cursor: pointer;
        font: inherit;
        text-decoration: underline;
        padding: 0;
      }
      .btn-link--sm { font-size: 11px; }
      .render-params-warn {
        margin-top: 6px;
        font-size: 11px;
        color: var(--color-warning);
      }

      /* Run params chips on the viewer header */
      .render-viewer-header-main {
        display: flex;
        flex-direction: column;
        gap: 6px;
        min-width: 0;
      }
      .run-params-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
      }
      .run-params-chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-family: var(--font-mono);
        font-size: 10px;
        padding: 2px 6px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: 4px;
        max-width: 220px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .run-params-chip-key { color: var(--color-accent); }
      .run-params-chip-val { color: var(--color-text); }

      /* History row with copy-params affordance */
      .render-history-row-wrap {
        position: relative;
      }
      .render-history-copy {
        position: absolute;
        top: 6px;
        right: 6px;
        font-size: 10px;
        padding: 2px 6px;
        background: var(--color-surface-0);
        border: 1px solid var(--color-border-subtle);
        border-radius: 3px;
        color: var(--color-text-secondary);
        cursor: pointer;
        opacity: 0;
        transition: opacity 0.12s ease;
      }
      .render-history-row-wrap:hover .render-history-copy { opacity: 1; }
      .render-history-copy:hover {
        color: var(--color-accent);
        border-color: var(--color-accent);
      }

      @keyframes spin { to { transform: rotate(360deg); } }
    `}</style>
  );
}
